import { Accordion, Clickable, H2, PortableText, TypeWrapper } from '@ahmdigital/ui';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { map } from 'lodash/fp';
import { useState } from 'react';

import { AccordionContent, ColumnWrapper, ContentWrapper, SummaryWrapper } from './styles';
import { ImageType } from '../../../types/image';
import { MappedQuestion } from '../../../types/question';
import { PortableTextType } from '../../../types/sanityInternal';
import accordionActionCreators from '../../../actions/accordion';
import AccordionImage from './components/accordion-image';
import getVisibleAccordionItems from './utils/get-visible-accordion-items';
import styles from './styles.module.sass';
import useIsAfterHydration from '../../../hooks/use-is-after-hydration';
import wrapItemBody from './components/wrap-item-body';
import WrappedSection from '../wrapped-section';

type AccordionSectionProps = {
  accordionId: string;
  actions: {
    accordion: {
      updateAccordionWithId: ({ id, openIndices }: { id: string; openIndices: string[] }) => void;
    };
  };
  ariaLabelPrefix?: string;
  className?: string;
  cta?: {
    title: string;
    to: string;
  };
  image?: ImageType;
  initialVisible?: number;
  isTwoColumn?: boolean;
  items?: MappedQuestion[];
  minimalBorder?: boolean;
  openIndices?: number[];
  title?: string;
  spacerSize?: number | 'None';
  summary?: PortableTextType[];
  withBorder?: boolean;
};

const AccordionSection = ({
  actions,
  accordionId,
  ariaLabelPrefix,
  className,
  cta,
  image,
  minimalBorder,
  openIndices = [],
  items = [],
  summary,
  title = '',
  withBorder = false,
  isTwoColumn,
  initialVisible,
  spacerSize,
}: AccordionSectionProps) => {
  const isAfterHydration = useIsAfterHydration();
  const [isOpen, setIsOpen] = useState(false);
  const itemsToDisplay = getVisibleAccordionItems({ initialVisible, isOpen, items });
  const showSeeMore = initialVisible && items.length > initialVisible;
  const hasHeaderCopy = title || summary;

  if (!isAfterHydration) {
    return null;
  }

  return (
    <WrappedSection spacerSize={spacerSize} withBorder={withBorder}>
      {!isTwoColumn && <AccordionImage image={image} />}
      {hasHeaderCopy && (
        <TypeWrapper variant="center">
          {title && <H2 variant={`marginBottomSpacer${summary ? '8' : '6'}`}>{title}</H2>}
          {summary && (
            <SummaryWrapper>
              <PortableText variant="marginBottomSpacer8" source={summary} />
            </SummaryWrapper>
          )}
        </TypeWrapper>
      )}
      <ContentWrapper isTwoColumn={isTwoColumn} data-testid="content-wrapper">
        {isTwoColumn && (
          <ColumnWrapper isTwoColumn={isTwoColumn} data-testid="two-column-image">
            <AccordionImage image={image} isTwoColumn />
          </ColumnWrapper>
        )}
        <ColumnWrapper isTwoColumn={isTwoColumn}>
          <AccordionContent isTwoColumn={isTwoColumn}>
            <Accordion
              className={className}
              id={accordionId}
              ariaLabelPrefix={ariaLabelPrefix}
              items={map(
                (item) => ({
                  ...item,
                  body: wrapItemBody(item.body),
                }),
                itemsToDisplay,
              )}
              // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '(newOpenIndices: string[]) => void' is not assignable to type '(...args: unknown[]) => unknown'.
              updateSelection={(newOpenIndices: string[]) => {
                actions.accordion.updateAccordionWithId({
                  id: accordionId,
                  openIndices: newOpenIndices,
                });
              }}
              variant={[!minimalBorder && 'fullBorder', 'white']}
              {...{ openIndices }}
            />
            {!isOpen && showSeeMore && (
              <TypeWrapper variant={['center', 'marginTopSpacer8']}>
                <Clickable
                  // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '{ fontWeight: string; name: string; position: "right"; }' is not assignable to type '{ name: string; position?: "top" | "left" | "right" | undefined; size?: string | undefined; }'.
                  icon={{ fontWeight: 'regular', name: 'chevron-down', position: 'right' }}
                  isUnstyled
                  onClick={() => setIsOpen(true)}
                >
                  View more
                </Clickable>
              </TypeWrapper>
            )}
            {cta?.title && cta?.to && (
              <div className={styles.cta}>
                {/* @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '{ fontWeight: string; name: string; position: "right"; }' is not assignable to type '{ name: string; position?: "top" | "left" | "right" | undefined; size?: string | undefined; }'. */}
                <Clickable icon={{ fontWeight: 'regular', name: 'chevron-right', position: 'right' }} to={cta.to}>
                  {cta.title}
                </Clickable>
              </div>
            )}
          </AccordionContent>
        </ColumnWrapper>
      </ContentWrapper>
    </WrappedSection>
  );
};

// @ts-expect-error - Automatic, Please fix when editing this file
const mapStateToProps = (state, { accordionId }) => ({
  openIndices: state?.accordion?.openIndices?.[accordionId],
});

// @ts-expect-error - Automatic, Please fix when editing this file
const mapDispatchToProps = (dispatch) => ({
  actions: {
    accordion: bindActionCreators(accordionActionCreators, dispatch),
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(AccordionSection);

export { AccordionSection as AccordionSectionForTest };
